<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-box-arrow-right me-2"></i>Transfers Management
      </h4>
      <div class="d-flex">
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="transferModal = true; transferMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Transfer
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading transfers...</p>
      </div>

      <!-- Transfers Content -->
      <div v-if="!loading" class="transfers-content fade-in">
        <!-- Transfers Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ paginationData.total || transferData.length }}</span>
            <span class="text-muted">Transfers</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search transfer" class="mb-0 w-auto" />
        </div>

        <!-- Transfers Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>TO STORE</th>
                  <th>BY USER</th>
                  <th>DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in paginatedTransfers" :key="row[0]" class="transfer-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ row[0].split("-").pop() }}</td>
                  <td>{{ row[1] }}</td>
                  <td>{{ row[2] }}</td>
                  <td>{{ row[3] }}</td>
                  <td>{{ row[4] }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(row[5])">
                      {{ row[5] }}
                    </Badge>
                  </td>
                  <td>{{ row[6] }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="transferDetails(row[0])">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Add Transfer Modal -->
    <Modal :open="transferModal" @closed="transferModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="transferMode === 'add'">
          <i class="bi bi-plus-circle me-2"></i>Add Transfer
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2"></i>Edit Transfer
        </h5>
        <button @click="transferModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="code" class="form-label fw-bold">Transfer Code:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-upc"></i></span>
                  <input type="text" class="form-control" placeholder="Code" v-model="transfer.code" readonly>
                </div>
                <small class="form-text text-muted">Auto-generated code</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="store" class="form-label fw-bold">Destination Store:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-shop"></i></span>
                  <select class="form-select" v-model="transfer.store_id" data="">
                    <option value="" disabled selected>Select a store</option>
                    <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.name }}</option>
                  </select>
                </div>
                <small class="form-text text-muted">Select the store to transfer items to</small>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12">
              <div class="form-group mb-3">
                <label for="description" class="form-label fw-bold">Description:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-card-text"></i></span>
                  <textarea class="form-control" placeholder="Enter transfer description" v-model="transfer.description" rows="3"></textarea>
                </div>
                <small class="form-text text-muted">Provide details about this transfer</small>
              </div>
            </div>
          </div>

          <!-- Summary Section -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="alert alert-info d-flex align-items-center">
                <i class="bi bi-info-circle-fill me-2"></i>
                <div>
                  <strong>Note:</strong> After creating the transfer, you'll be able to add products to it.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="transferModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="transferMode === 'add'" @click="createTransfer">
          <i class="bi bi-plus-circle me-1"></i> Create Transfer
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateTransfer(transfer.id)">
          <i class="bi bi-check-circle me-1"></i> Update Transfer
        </button>
      </div>
    </Modal>
    <!-- Add Transfer Modal End -->

  </Card>
</template>

<script>
import transferService from "@/api/transferService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import storeService from "@/api/storeService";
import formatDate from "@/utils/formatDate";

export default {
  name: "Transfers",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      stores: [],
      profile: {},
      loading: true,
      transfer: {
        organization_id: this.organization.id,
        store_id: '',
        user_id: this.profile.id,
      },
      transferMode: 'add',
      transferModal: false,
      dataTableHeaders: [
        '#',
        'Code',
        'To Store',
        'By User',
        'Description',
        'Status',
        'Date',
        null
      ],
      dataTable: [],
      paginationData: {},
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
      searchTimeout: null,
    }
  },

  methods:{
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getTransfers() {
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage
      }
      
      if (this.dataTableSearch) {
        params.search = this.dataTableSearch
      }
      
      const response = await transferService.getTransfers(params)
      // Handle paginated response structure
      if (response && response.data) {
        this.dataTable = response.data
        this.paginationData = {
          current_page: response.current_page,
          last_page: response.last_page,
          per_page: response.per_page,
          total: response.total
        }
      } else {
        // Fallback for non-paginated response (backward compatibility)
        this.dataTable = Array.isArray(response) ? response : []
        this.paginationData = {}
      }
      
      let nextNo = await transferService.getTransferNextNo()
      this.transfer.code = nextNo.next
      this.loading = false
    },
    transferDetails(id) {
      router.push('/transfer/' + id)
    },
    async createTransfer() {
      let response = await transferService.createTransfer(this.transfer)
      if (response) {
        this.transferModal = false
        await this.getTransfers()
      }
    },
  },

  computed: {
    transferData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].id,
          this.dataTable[i].code,
          this.dataTable[i].store.code + ' - ' + this.dataTable[i].store.name,
          this.dataTable[i].user.first_name + ' ' + this.dataTable[i].user.last_name,
          this.dataTable[i].description,
          this.dataTable[i].status,
          formatDate(this.dataTable[i].created_at),
        ];
        data.push(fields);
      }
      return data;
    },
    filteredTransfers(){
      // With server-side pagination, filtering is done by the API
      // Return the data as-is for server-side, or filter client-side as fallback
      if (this.paginationData.total !== undefined) {
        return this.transferData;
      }
      
      // Fallback to client-side filtering for backward compatibility
      if (!this.dataTableSearch) return this.transferData;
      const term = this.dataTableSearch.toLowerCase();
      return this.transferData.filter(t =>
          (t[1] && t[1].toLowerCase().includes(term)) ||
          (t[2] && t[2].toLowerCase().includes(term)) ||
          (t[3] && t[3].toLowerCase().includes(term)) ||
          (t[4] && t[4].toLowerCase().includes(term))
      );
    },
    paginatedTransfers(){
      // With server-side pagination, return data as-is
      if (this.paginationData.total !== undefined) {
        return this.filteredTransfers
      }
      
      // Fallback to client-side pagination for backward compatibility
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredTransfers.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      // If we have server-side pagination data, use it
      if (this.paginationData.last_page !== undefined) {
        return this.paginationData.last_page
      }
      
      // Fallback to client-side pagination for backward compatibility
      return Math.ceil(this.filteredTransfers.length / this.dataTablePerPage) || 1;
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.getTransfers();
    },
    dataTablePage() {
      this.getTransfers();
    },
    dataTableSearch: {
      handler() {
        this.dataTablePage = 1;
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getTransfers();
        }, 500);
      }
    }
  },

  created: async function () {
    await this.getTransfers()
    this.stores = await storeService.getStores();
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Transfers page styles */
.transfers-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.transfer-row {
  transition: all 0.2s ease;
}

.transfer-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
